/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

// import { Player, PlayerEvent } from 'bitmovin-player';
// import { UIManager } from 'bitmovin-player-ui';
import { VideoPlayerService } from 'app/shared/services/video-player.service';

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { environment } from 'environments/environment';
import { UtilsService } from 'app/shared/services/utils.service';
import { AssetsService, GoogleAnalyticsService, VideoService } from 'app/shared/services';
import { BehaviorSubject } from 'rxjs';
import { Constants } from 'app/shared/models/constants.model';

declare var bitmovin: any;

@Component({
  selector: '[video-player]',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
})
export class VideoPlayerComponent implements OnInit, AfterViewInit, OnChanges {
  @Input()
  source: string;
  @Input()
  stream: string;
  @Input()
  autoplay = false;
  @Input()
  videoId: string;
  @Input()
  controls: boolean;
  @Input()
  tapcontrols = true;
  @Input()
  multibitrate = false;
  @Input()
  borderRadius = true;
  @Input()
  analytics = true;
  @Input()
  title: string;
  @Input()
  video: any;
  @Input()
  preload = true;
  @Input()
  muted = false;
  @Input()
  poster = false;
  @Input()
  posterSource: string;
  @Input()
  dontLoop = false;
  @Input()
  startAt: number;
  @Input() trackViews = true;
  @Input() videoDate: Date;
  @Input() nextMoment: number;
  @Input() prevMoment: number;

  @Output() getReference = new EventEmitter();
  @Output() setMetadata = new EventEmitter();
  @Output() onError = new EventEmitter();
  @Output() onPlaybackFinished = new EventEmitter();
  @Output() onPlay = new EventEmitter();
  @Output() onPause = new EventEmitter();
  @Output() onSeek = new EventEmitter();
  @Output() onNextMoment = new EventEmitter();
  @Output() onPrevMoment = new EventEmitter();

  @ViewChild('playerContainer') playerContainer: ElementRef;
  @ViewChild('nextMomentTpl') nextMomentTpl: ElementRef;
  @ViewChild('prevMomentTpl') prevMomentTpl: ElementRef;

  playerConfig: any = {
    controls: true,
    tapcontrols: true,
  };

  private player: any;
  private playerUi: any;
  private analyticsSubject = new BehaviorSubject(false);
  private viewsIncremented = false;
  private playerEvents;
  private bitrates;

  constructor(
    private assetsService: AssetsService,
    public videoService: VideoService,
    private utilsService: UtilsService,
    private gaService: GoogleAnalyticsService,
    private videoPlayerService: VideoPlayerService,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.playerConfig = {
      ...this.playerConfig,
      key: environment.bitmovinPlayerKey,
      playback: {
        autoplay: this.autoplay,
        muted: this.muted || this.utilsService.hasSmallResolution() || window['safari'] !== undefined,
      },
      ui: false,
    };

    if (this.analytics && environment.production) {
      this.playerConfig.analytics = {
        key: environment.bitmovinAnalyticsKey,
        videoId: this.videoId,
        title: this.getAnalyticsVideoTitle(),
        customData1: this.getAnalyticsVideoType(),
      };
    }

    if (this.controls === false) {
      this.playerConfig.controls = this.controls;
    }

    if (this.tapcontrols === false) {
      this.playerConfig.tapcontrols = this.tapcontrols;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      this.player &&
      changes &&
      ((changes.source && changes.source.currentValue) || (changes.videoId && changes.videoId.currentValue))
    ) {
      this.player
        .load(this.loadSource())
        .then(() => {
          // source is changed. So we should track views to a asset
          this.viewsIncremented = false;

          if (this.autoplay) {
            this.player.play();
          }
        })
        .catch((err) => {
          if (err) {
            console.error(err);
            return;
          }

          // tslint:disable-next-line: no-console
          console.info('Video player was destoryed while loading');
        });
    }
    // if (changes.nextMoment) {
    //   this.nextMomentForceShow = true;
    //   this.cdr.detectChanges();
    //   if (this.nextMomentTimeout) {
    //     clearTimeout(this.nextMomentTimeout);
    //   }
    //   this.nextMomentTimeout = setTimeout(() => {
    //     this.nextMomentForceShow = false;
    //     this.cdr.detectChanges();
    //   }, 5000);
    // }
  }

  ngAfterViewInit() {
    this.initializePlayer()
      .then(() => {
        this.player.on(this.playerEvents.Ready, () => {
          this.getReference.emit({ player: this.player });
        });

        this.analyticsSubject.subscribe((analyticsScriptReady) => {
          if (analyticsScriptReady) {
            this.analyticsSubject.complete();
            if (this.analytics) {
              bitmovin.analytics.augment(this.player);
            }
          }
        });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  ngOnDestroy() {
    if (this.player) {
      this.player.pause();
      this.player
        .destroy()
        .then(() => {
          this.playerUi.release();
        })
        .catch((err) => {
          if (err) {
            console.error(err);
            return;
          }

          // tslint:disable-next-line: no-console
          console.info('Video player was destoryed while loading');
        });
    }
    this.analyticsSubject.unsubscribe();
  }

  loadSource() {
    if (this.stream) {
      /* remove this when the response will have the correct URL */
      this.stream = this.stream.replace('http://35.230.127.184', environment.liveStreamInstance1); // NOSONAR
      this.stream = this.stream.replace('http://35.203.174.195', environment.liveStreamInstance2); // NOSONAR
      return {
        hls: this.stream,
      };
    } else if (this.video && this.video.url && !this.video.url.startsWith(environment.liveStreamHostname)) {
      /* TODO: delete after fixing urls that are stored with suffix; check video-library component for the same issue*/
      const trimmedUrl = this.video.url
        .replace(`${Constants.VIDEO_QUALITY.q720_30}.mp4`, '.mp4')
        .replace(`${Constants.VIDEO_QUALITY.q720_60}.mp4`, '.mp4');
      let fastlySource = trimmedUrl
        .replace(Constants.GS_ASSET_HOSTNAME + '/video_assets', environment.fastly.main)
        .split('.mp4')[0];
      fastlySource = fastlySource.replace(/#/g, '%23');

      const fileFolderIndex = this.video.url.lastIndexOf('/');

      const suffix = this.video.is60fps ? Constants.VIDEO_QUALITY.q720_60 : Constants.VIDEO_QUALITY.q720_30;
      let thumbnailsPath =
        this.video.url.substring(0, fileFolderIndex) +
        '/thumbnails' +
        /* folder name - same as the file name (without .mp4) */
        this.video.url.substring(fileFolderIndex, this.video.url.length - 4) +
        /* quality suffix */
        (this.video.url.substring(-15, -4) === Constants.VIDEO_QUALITY.q720_30 ||
        this.video.url.substring(-15, -4) === Constants.VIDEO_QUALITY.q720_60
          ? ''
          : suffix) +
        '/';
      /* remove gcp hostname */
      thumbnailsPath = thumbnailsPath.replace(Constants.GS_ASSET_HOSTNAME + '/video_assets', '');
      thumbnailsPath = thumbnailsPath.replace(environment.fastly.main, '');

      thumbnailsPath =
        environment.fastly.main +
        thumbnailsPath
          .split('/')
          .map((url) => encodeURIComponent(url))
          .join('/') +
        'thumbnail.vtt';

      const source: any = {
        poster: this.poster ? this.posterSource : null,
        thumbnailTrack: {
          url: thumbnailsPath,
        },
      };

      if (this.utilsService.isMobileOrTablet()) {
        const bitrateSuffixes = [];
        if (this.bitrates._480p_30fps) {
          bitrateSuffixes.push(Constants.VIDEO_QUALITY.q480_30);
        }
        if (this.bitrates._720p_30fps) {
          bitrateSuffixes.push(Constants.VIDEO_QUALITY.q720_30);
        }
        if (this.bitrates._720p_60fps) {
          bitrateSuffixes.push(Constants.VIDEO_QUALITY.q720_60);
        }
        /* use adaptive bitrate by Fastly: mobile devices don't have decoding capabilities for high bitrate videos */
        const hls = `${fastlySource},${bitrateSuffixes.join(',')},master.m3u8`;
        source.hls = hls;
      } else {
        const progressive = [
          {
            url: `${fastlySource}${
              this.bitrates?._720p_60fps ? Constants.VIDEO_QUALITY.q720_60 : Constants.VIDEO_QUALITY.q720_30
            }.mp4`,
            type: 'video/mp4',
            bitrate: 5000000,
            label: '720p',
            preferred: true,
          },
        ];
        if (this.bitrates?._480p_30fps) {
          progressive.push({
            url: `${fastlySource}${Constants.VIDEO_QUALITY.q480_30}.mp4`,
            type: 'video/mp4',
            bitrate: 2000000,
            label: '480p',
            preferred: false,
          });
        }
        source.progressive = progressive;
      }
      return source;
    } else {
      return {
        progressive: this.source,
        poster: this.poster ? this.posterSource : '',
      };
    }
  }

  isNumber(val): boolean {
    return typeof val === 'number';
  }

  private async initializePlayer() {
    const loadSource = () => {
      this.player
        .load(this.loadSource())
        .then(() => {
          if (this.preload) {
            this.player.preload();
          }

          if (this.autoplay) {
            this.player.play();
          }
        })
        .catch((err) => {
          if (err) {
            console.error(err);
            return;
          }

          // tslint:disable-next-line: no-console
          console.info('Video player was destoryed while loading');
        });
    };

    const { Player, PlayerEvent } = await import('bitmovin-player');
    const { UIManager } = await import('bitmovin-player-ui');

    this.playerEvents = PlayerEvent;

    this.player = new Player(this.playerContainer.nativeElement, this.playerConfig);
    const uiContainer = await this.videoPlayerService.getUi(this.player);
    this.playerUi = new UIManager(this.player, uiContainer);

    // appended prev / next moment container after video player to be able to query it by player container
    this.renderer.appendChild(this.playerContainer.nativeElement, this.nextMomentTpl.nativeElement);
    this.renderer.appendChild(this.playerContainer.nativeElement, this.prevMomentTpl.nativeElement);

    if (!this.video || !this.video.url) {
      loadSource();
    } else {
      this.utilsService.checkAvailableBitrates(this.video.url).subscribe(
        (availableBitrates: any) => {
          if (availableBitrates.data) {
            this.bitrates = availableBitrates.data;
          }
          loadSource();
        },
        () => {
          loadSource();
        }
      );
    }

    this.player.on(PlayerEvent.PlaybackFinished, (finished) => {
      if (this.autoplay && !this.dontLoop) {
        this.player.play();
      }
      this.onPlaybackFinished.emit(finished);
    });

    this.player.on(PlayerEvent.Play, ($event) => {
      if (!this.viewsIncremented && this.trackViews) {
        this.assetsService.incrementViews(parseInt(this.videoId, 10)).subscribe();
        this.viewsIncremented = true;
      }
      this.onPlay.emit($event);
      this.gaService.sendEvent('Video', 'Play', this.video.type);
    });

    this.player.on(PlayerEvent.Paused, ($event) => {
      this.onPause.emit($event);
    });

    this.player.on(PlayerEvent.Seek, ($event) => {
      this.onSeek.emit($event);
    });

    this.player.on(PlayerEvent.Ready, () => {
      if (this.startAt) {
        this.player.seek(this.startAt);
      }
      this.setMetadata.emit({ duration: this.player.getDuration(), player: this.player });
    });

    this.player.on(PlayerEvent.Error, (error) => {
      this.onError.emit(error);
    });
  }

  private getAnalyticsVideoType() {
    let type = 'Other';
    if (this.video && this.video.type) {
      type = this.video.type;
    }
    return type;
  }

  private getAnalyticsVideoTitle() {
    let videoTitle = '';
    if (this.video) {
      videoTitle = this.video.name;
      if (this.video.eventName) {
        videoTitle += ' - ' + this.video.eventName;
      }
    }
    return videoTitle;
  }
}
